function buildWSURL(baseHTTPUrl) {
  var trimmed = (baseHTTPUrl || "").replace(/\/+$/, "");
  if (trimmed.indexOf("https://") === 0) {
    return "wss://" + trimmed.slice("https://".length) + "/print";
  }
  throw makeError("PRINT_APP_UNREACHABLE", "Chit Chats Print requires a secure local HTTPS endpoint");
}

function formatTemplate(template, vars) {
  if (!template) {
    return "";
  }
  return Object.keys(vars).reduce(function(acc, key) {
    return acc.replace("%{" + key + "}", vars[key]);
  }, template);
}

function shipmentProgressMessage(message, evt) {
  return formatTemplate(message("preparing_shipments_progress"), {
    fetched: evt.shipments_printed || evt.fetched || 0,
    total: evt.shipment_count || evt.total || 0
  }) || message("preparing_shipment_labels");
}

function fetchProgressMessage(message, evt) {
  if (evt.shipments_printed || evt.shipment_count) {
    return shipmentProgressMessage(message, evt);
  }
  return formatTemplate(message("downloading_progress"), {
    fetched: evt.fetched || 0,
    total: evt.total || 0
  }) || message("downloading_labels");
}

function failureProgressMessage(message, evt) {
  return formatTemplate(message("downloading_failure"), {
    failed: evt.failed || 0,
    total: evt.total || 0
  });
}

// Lookup table for non-terminal progress events; flat dispatch keeps the handler under the complexity budget.
var PROGRESS_MESSAGE_BUILDERS = {
  fetch_progress: fetchProgressMessage,
  merging: function(message) {
    return message("combining_labels");
  }
};

function progressMessageForEvent(message, evt) {
  var builder = PROGRESS_MESSAGE_BUILDERS[evt.type];
  return builder ? builder(message, evt) : "";
}

function defaultedOption(options, key, fallback) {
  if (options && typeof options[key] !== "undefined" && options[key] !== null) {
    return options[key];
  }
  return fallback;
}

function settleOnce(state) {
  return function(resolveOrReject, value) {
    if (state.settled) {
      return;
    }
    state.settled = true;
    resolveOrReject(value);
  };
}

function publishProgress(onProgress, message) {
  if (typeof onProgress === "function" && message) {
    onProgress(message);
  }
}

function makeError(code, message) {
  var err = new Error(message || "");
  err.code = code;
  return err;
}

function renderPartialFailureMessage(message, printed, failed, total) {
  var template = message("partial_failure");
  if (template) {
    return formatTemplate(template, { printed: printed, failed: failed, total: total });
  }
  return failed + " of " + total + " pages failed; " + printed + " printed";
}

function makePartialFailureError(message, printed, failed, total) {
  return makeError("PARTIAL_FAILURE", renderPartialFailureMessage(message, printed, failed, total));
}

function emitStaticProgress(evt, state) {
  publishProgress(state.onProgress, progressMessageForEvent(state.message, evt));
}

var EVENT_HANDLERS = {
  accepted: function(evt, state) {
    state.jobId = evt.job_id || state.jobId;
    if (typeof state.onAccepted === "function") {
      state.onAccepted(evt);
    }
    publishProgress(state.onProgress, state.message("preparing_label_download"));
  },
  // Concurrent print app fetchers race between incFetched() and the channel
  // send, so a lower fetched count can land after a higher one. Drop the
  // stale ones so the modal counter stays monotonic.
  fetch_progress: function(evt, state) {
    var fetched = evt.shipments_printed || evt.fetched || 0;
    if (fetched <= state.lastFetchedShown) {
      return;
    }
    state.lastFetchedShown = fetched;
    emitStaticProgress(evt, state);
  },
  merging: emitStaticProgress,
  printing: function(_evt, state) {
    var rawPrinter = state.contentType === "zpl";
    var messageKey = rawPrinter ? "sending_to_printer" : "opening_print_preview";
    publishProgress(state.onProgress, state.message(messageKey));
  },
  preview_ready: function(evt, state) {
    if (typeof state.onPreviewReady === "function" && evt.preview_url) {
      state.onPreviewReady(evt.preview_url);
    }
  },
  fetch_failed: function(evt, state) {
    publishProgress(state.onProgress, failureProgressMessage(state.message, evt));
  },
  completed: function(evt, state) {
    var printed = evt.printed || 0;
    var failed = evt.failed || 0;
    var total = evt.total || 0;
    if (failed > 0) {
      state.settle(state.reject, makePartialFailureError(state.message, printed, failed, total));
      return;
    }
    state.settle(state.resolve, { jobId: state.jobId, printed: printed, failed: failed, total: total });
  },
  failed: function(evt, state) {
    var error = makeError(
      evt.error_code || "PRINT_FAILED",
      evt.error_message || state.message("print_command_failed")
    );
    state.settle(state.reject, error);
  },
  canceled: function(_evt, state) {
    var rawPrinter = state.contentType === "zpl";
    if (rawPrinter) {
      state.settle(state.reject, makeError("USER_CANCELED", state.message("zpl_canceled")));
      return;
    }
    state.settle(state.resolve, { jobId: state.jobId, canceled: true });
  }
};

function handleEvent(evt, state) {
  var handler = EVENT_HANDLERS[evt.type];
  if (handler) {
    handler(evt, state);
  }
}

function attachWSHandlers(socket, state, command) {
  socket.onopen = function() {
    state.opened = true;
    socket.send(JSON.stringify(command));
  };

  socket.onmessage = function(messageEvent) {
    var evt = parseEvent(messageEvent);
    if (evt) {
      handleEvent(evt, state);
    }
  };

  socket.onerror = function() {
    // After the initial accept, an error usually precedes onclose; let
    // onclose decide the final disposition so we don't double-settle.
    if (!state.opened) {
      state.settle(state.reject, makeError("PRINT_APP_UNREACHABLE", state.message("print_app_unreachable")));
    }
  };

  socket.onclose = function() {
    state.settle(state.reject, makeError("PRINT_APP_DROPPED", state.message("print_app_unreachable")));
  };
}

function parseEvent(messageEvent) {
  try {
    return JSON.parse(messageEvent.data || "{}");
  } catch (_e) {
    return null;
  }
}

function rejectionReasons(message) {
  return {
    missingPrinter: makeError("PRINTER_REQUIRED", message("select_printer_error")),
    noWebSocket: makeError("WEBSOCKET_UNSUPPORTED", message("print_app_unavailable"))
  };
}

function preflightError(printerId, contentType, message) {
  var reasons = rejectionReasons(message);
  // Only raw-printer ZPL flows need a printer_id up front — Chit Chats Print sends raw bytes
  // straight to that queue. PDF prints route through the OS print dialog
  // where the user picks the printer, so a missing/stale device_id in the
  // modal must not block the flow before we've even opened the WS.
  if (contentType === "zpl" && !printerId) {
    return reasons.missingPrinter;
  }
  if (typeof window.WebSocket === "undefined") {
    return reasons.noWebSocket;
  }
  return null;
}

function startSession(apiClient, command, onProgress, options, message) {
  return new Promise(function(resolve, reject) {
    var state = {
      settled: false,
      opened: false,
      jobId: null,
      lastFetchedShown: 0,
      onProgress: onProgress,
      onAccepted: (options && options.onAccepted) || null,
      onPreviewReady: (options && options.onPreviewReady) || null,
      contentType: command.content_type,
      message: message,
      resolve: resolve,
      reject: reject
    };
    state.settle = settleOnce(state);

    var socket = openSocket(apiClient.baseUrl(), state);
    if (socket) {
      attachWSHandlers(socket, state, command);
    }
  });
}

function openSocket(baseUrl, state) {
  try {
    return new window.WebSocket(buildWSURL(baseUrl));
  } catch (_e) {
    state.settle(state.reject, makeError("PRINT_APP_UNREACHABLE", state.message("print_app_unreachable")));
    return null;
  }
}

function buildPrintCommand(payload, cmd, printerId, contentType, options) {
  return Object.assign({}, payload, {
    cmd: cmd,
    type: cmd,
    printer_id: printerId || "",
    content_type: contentType,
    client_job_ref: defaultedOption(options, "clientJobRef", "cc_print_" + Date.now()),
    copies: defaultedOption(options, "copies", 1),
    concurrency: defaultedOption(options, "concurrency", 0)
  });
}

function runWSFlow(apiClient, message, payload, printerId, options, config) {
  var contentType = config.contentType;
  var blocker = preflightError(printerId, contentType, message);
  if (blocker) {
    return Promise.reject(blocker);
  }

  var onProgress = (options && options.onProgress) || null;
  publishProgress(onProgress, message(config.progressMessage));
  var command = buildPrintCommand(payload, config.cmd, printerId, contentType, options);

  return startSession(apiClient, command, onProgress, options, message);
}

function createPrintWSFlow(apiClient, message) {
  return {
    runURLs(payload, printerId, options) {
      return runWSFlow(apiClient, message, payload, printerId, options, {
        cmd: "print",
        contentType: payload.content_type,
        progressMessage: "preparing_label_download"
      });
    },
    runShipments(payload, printerId, options) {
      return runWSFlow(apiClient, message, payload, printerId, options, {
        cmd: "print_shipments",
        contentType: payload.format === "zpl" ? "zpl" : "png",
        progressMessage: "preparing_shipment_labels"
      });
    }
  };
}
export { createPrintWSFlow };
